/**
 * Plan definitions for frontend display
 * These should match the backend plan definitions
 */

export type PlanId = 'free' | 'pro' | 'team';

export interface PlanFeatures {
  prioritySupport: boolean;
  teamSeats: number;
}

export interface Plan {
  id: PlanId;
  name: string;
  // monthlyMinutes is an invisible abuse backstop — never display it in the UI
  monthlyMinutes: number;
  runsPerMonth: number;
  emailsPerMonth: number;
  maxIdentities: number;
  maxConcurrentSessions: number;
  features: PlanFeatures;
  priceMonthly: number;
  highlighted?: boolean;
  description?: string;
}

/**
 * Plan definitions
 */
export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    monthlyMinutes: 900,
    runsPerMonth: 120,
    emailsPerMonth: 150,
    maxIdentities: 9,
    maxConcurrentSessions: 1,
    features: {
      prioritySupport: false,
      teamSeats: 1,
    },
    priceMonthly: 0,
    description: 'Give your first agent a real identity',
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    monthlyMinutes: 1500,
    runsPerMonth: 200,
    emailsPerMonth: 1000,
    maxIdentities: 25,
    maxConcurrentSessions: 3,
    features: {
      prioritySupport: false,
      teamSeats: 1,
    },
    priceMonthly: 79,
    highlighted: true,
    description: 'For agents running in production',
  },
  team: {
    id: 'team',
    name: 'Team',
    monthlyMinutes: 7500,
    runsPerMonth: 1000,
    emailsPerMonth: 5000,
    maxIdentities: 100,
    maxConcurrentSessions: 10,
    features: {
      prioritySupport: true,
      teamSeats: 5,
    },
    priceMonthly: 299,
    description: 'For fleets of agents and teams',
  },
};

/**
 * Get all plans sorted by price
 */
export function getAllPlans(): Plan[] {
  return Object.values(PLANS).sort((a, b) => a.priceMonthly - b.priceMonthly);
}

/**
 * Format price for display
 */
export function formatPrice(price: number): string {
  if (price === 0) return 'Free';
  return `$${price}/mo`;
}

/**
 * Format minutes for display
 */
export function formatMinutes(minutes: number): string {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${minutes} min`;
}
